import { Activity, HeartPulse, Stethoscope, ShieldPlus, Sparkles, Handshake, BadgeCheck, Users, BedDouble, BriefcaseMedical } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const COVERAGE = [
  { icon: Activity,           title: "Invalidità permanente da infortunio", desc: "Indennizzo in caso di invalidità permanente conseguente a un infortunio, in ambito privato o professionale." },
  { icon: BedDouble,           title: "Indennità da ricovero",                desc: "Diaria giornaliera in caso di ricovero ospedaliero per infortunio o malattia, anche in day hospital." },
  { icon: BriefcaseMedical,    title: "Rimborso spese mediche",               desc: "Spese di cura, accertamenti, interventi e ricoveri, in strutture pubbliche e private convenzionate." },
  { icon: HeartPulse,           title: "Malattia e patologie gravi",           desc: "Capitale o rendita in caso di diagnosi di patologie gravi previste in polizza (oncologia, cardiologia, neurologia)." },
  { icon: Stethoscope,          title: "Prevenzione e check-up",               desc: "Pacchetti di visite specialistiche, esami diagnostici e medicina preventiva inclusi in molte soluzioni." },
  { icon: ShieldPlus,           title: "Decesso da infortunio",                desc: "Capitale ai beneficiari in caso di decesso conseguente a infortunio, integrabile alla copertura vita." },
];

const WHY = [
  { icon: Handshake,  title: "Capiamo prima cosa serve",  desc: "Lavoro, sport, abitudini, situazione familiare: la copertura nasce da qui, non da un pacchetto standard." },
  { icon: Sparkles,   title: "Garanzie modulabili",       desc: "Solo infortuni, solo malattia o una soluzione integrata: scegliamo insieme cosa includere e con quali massimali." },
  { icon: BadgeCheck, title: "Chiarezza sulle clausole",  desc: "Esclusioni, franchigie, periodi di carenza e modalità di liquidazione: te lo spieghiamo prima della firma." },
  { icon: Users,      title: "Più compagnie a confronto", desc: "Confrontiamo le condizioni delle principali compagnie e proponiamo la più equilibrata per il tuo profilo." },
];

const FAQS = [
  { q: "Qual è la differenza tra polizza infortuni e polizza malattia?",
    a: "La polizza infortuni copre le conseguenze economiche di eventi traumatici, accidentali e involontari (cadute, incidenti stradali, infortuni sul lavoro, sportivi, domestici): può prevedere indennità per invalidità permanente, diaria da ricovero, rimborso spese e capitale in caso di decesso. La polizza malattia copre invece le conseguenze economiche di patologie, con rimborso spese mediche, indennità da ricovero, prestazioni specialistiche, prevenzione e capitali in caso di diagnosi di patologie gravi. Spesso le due coperture si integrano in un'unica soluzione." },
  { q: "A chi è consigliata?",
    a: "A chiunque desideri proteggere il proprio tenore di vita da imprevisti che incidono sulla capacità lavorativa o sulle spese sanitarie. È particolarmente utile per liberi professionisti e lavoratori autonomi (che non hanno copertura INAIL e cassa malattia), genitori e famiglie con figli, sportivi, dipendenti senza polizza aziendale, persone con storia familiare di patologie specifiche, chi svolge attività con esposizione al rischio." },
  { q: "Cosa si intende per franchigia e carenza?",
    a: "La franchigia è la parte di danno (o il numero di giorni di ricovero) che resta a carico dell'assicurato. La carenza è il periodo iniziale, dopo la sottoscrizione, durante il quale alcune garanzie non sono ancora operative (in genere 30-180 giorni per malattie, assente per infortuni). Sono parametri importanti che influenzano sia il costo della polizza sia la rapidità di tutela: in consulenza valutiamo insieme la combinazione più adatta." },
  { q: "È possibile dedurre i premi dalle tasse?",
    a: "Sì, in parte. I premi versati per polizze contro il rischio di invalidità permanente (non inferiore al 5%) o non autosufficienza sono detraibili al 19% fino a 530 € l'anno (1.291,14 € per la non autosufficienza). I premi per polizze sanitarie collettive aziendali godono di trattamenti fiscali differenti. In consulenza valutiamo i benefici fiscali specifici sul tuo caso." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni di consulenza" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function InfortuniMalattia() {
  useSeoMeta({
    title: "Polizza Infortuni e Malattia — Protezione personale | Da Pra Assicurazioni",
    description: "Polizza infortuni e malattia su misura: indennità invalidità, ricovero, rimborso spese mediche, prevenzione e patologie gravi. Consulenza dedicata, agenzia assicurativa plurimandataria dal 1958.",
    keywords: "polizza infortuni, polizza malattia, assicurazione sanitaria, polizza salute, rimborso spese mediche, invalidità permanente, polizza famiglia salute",
    canonical: "https://dapraassicurazioni.it/infortuni-malattia",
    jsonLd: JSON_LD, jsonLdId: "ld-infortuni",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Infortuni e Malattia", href: "/infortuni-malattia" }} />

      <LandingHero
        eyebrow="Polizza Infortuni e Malattia · Dal 1958"
        title={<>Tutela la tua salute <span className="italic">e il tuo tenore di vita.</span></>}
        description="Una copertura pensata per affrontare con serenità imprevisti, ricoveri e spese sanitarie. Scegliamo insieme cosa proteggere — infortuni, malattia o entrambi — con condizioni tarate sulle tue esigenze."
        imageSrc={HERO_IMG}
        imageAlt="Polizza Infortuni e Malattia — Da Pra Assicurazioni"
        ctaPrimaryLabel="Richiedi una consulenza"
        ctaPrimaryHref="/preventivo?type=infortuni"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Garanzie disponibili"
        heading="Cosa puoi proteggere."
        intro="Componi la copertura partendo dalle garanzie che contano davvero per te e la tua famiglia."
        items={COVERAGE}
      />

      <WhyUsSection
        eyebrow="Il nostro approccio"
        heading="Una consulenza che parte dall'ascolto."
        body={[
          "Da tre generazioni accompagniamo persone e famiglie nella scelta delle coperture personali. Conosciamo le specificità dei diversi profili: liberi professionisti, dipendenti, sportivi, famiglie con figli.",
          "Il punto di partenza è sempre lo stesso: capire la tua situazione, il tuo lavoro, le tue priorità. Solo dopo costruiamo la proposta, confrontando le condizioni delle compagnie con cui collaboriamo.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="La fiducia dei nostri clienti." />
      <FaqSection heading="Domande frequenti su infortuni e malattia." faqs={FAQS} />

      <Footer />
    </div>
  );
}
